import { fmtPrice } from "../lib/format";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n, catName } from "../lib/i18n";
import { useSEO } from "../lib/useSEO";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Star, Calendar, Plus, Wallet, ClipboardList, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { ProviderRequests } from "../components/admin/ProviderRequests";
import { Waitlist } from "../components/admin/Waitlist";
import { ProviderApplicationStatus } from "../components/ProviderApplicationStatus";

function StatCard({ label, value, hint }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5">
      <div className="eyebrow text-stone-500 text-xs">{label}</div>
      <div className="font-serif text-3xl mt-2">{value}</div>
      {hint && <div className="text-xs text-stone-500 mt-1">{hint}</div>}
    </div>
  );
}

export function CustomerDashboard() {
  useSEO({ title: "Dashboard | Refine Touch USA", robots: "noindex, follow" });
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [bookings, setBookings] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = () => api.get("/bookings").then((r) => setBookings(r.data));
  useEffect(() => { load(); }, []);

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewOpen.id, rating, comment });
      toast.success(t("review_published"));
      setReviewOpen(null); setComment("");
    } catch (e) { toast.error(e.response?.data?.detail || t("err_generic")); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-serif text-4xl">{t("hello")}, {user?.name}</h1>
      <p className="text-stone-600 text-sm">{t("track_bookings")}</p>

      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        <StatCard label={t("bookings")} value={bookings.length} />
        <StatCard label={t("upcoming")} value={bookings.filter((b) => ["pending","confirmed","in_progress"].includes(b.status)).length} />
        <StatCard label={t("completed")} value={bookings.filter((b) => b.status === "completed").length} />
      </div>

      <h2 className="font-serif text-2xl mt-10 mb-4">{t("my_bookings")}</h2>
      <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
        {bookings.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">{t("no_bookings")} <Link to="/services" className="underline">{t("explore_link")}</Link></div>}
        {bookings.map((b) => (
          <div key={b.id} data-testid={`booking-${b.id}`} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <img src={b.provider?.avatar} className="w-12 h-12 rounded-full object-cover" alt="" />
            <div className="flex-1">
              <div className="font-medium">{b.service_title}</div>
              <div className="text-xs text-stone-500">{t("with_who")} {b.provider?.name} · {new Date(b.scheduled_at).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}</div>
            </div>
            <div className="text-sm font-serif">{fmtPrice(b.price)}</div>
            <span className={`text-xs px-3 py-1 rounded-full ${b.status === "completed" ? "bg-emerald-50 text-emerald-800" : b.status === "cancelled" ? "bg-red-50 text-red-800" : "bg-stone-100 text-stone-700"}`}>{b.status}</span>
            {b.status === "completed" && (
              <Button data-testid={`review-${b.id}`} size="sm" variant="outline" onClick={() => setReviewOpen(b)}>{t("leave_review")}</Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!reviewOpen} onOpenChange={(o) => !o && setReviewOpen(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{t("your_review")}</DialogTitle></DialogHeader>
          <div>
            <div className="flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button key={n} data-testid={`rating-${n}`} onClick={() => setRating(n)}>
                  <Star size={28} className={n <= rating ? "fill-stone-800 text-stone-800" : "text-stone-300"} />
                </button>
              ))}
            </div>
            <Textarea className="mt-4" data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("share_exp")} rows={4} />
            <Button data-testid="submit-review" onClick={submitReview} className="mt-4 w-full bg-stone-900 text-stone-50">{t("publish_review")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ProviderDashboard() {
  useSEO({ title: "Provider Dashboard | Refine Touch USA", robots: "noindex, follow" });
  const { user, refresh } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category_id: "", price: 50, duration_minutes: 60, location: "", images: [""] });

  const loadAll = async () => {
    const [s, b, c] = await Promise.all([
      api.get("/services?include_all=true").then((r) => r.data.filter((x) => x.provider_id === user.id)),
      api.get("/bookings").then((r) => r.data),
      api.get("/categories").then((r) => r.data),
    ]);
    setServices(s); setBookings(b); setCats(c);
  };
  useEffect(() => { if (user) loadAll(); }, [user]);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", description: "", category_id: cats[0]?.id || "", price: 50, duration_minutes: 60, location: user.location || "", images: ["https://source.unsplash.com/800x600/?service"] });
    setOpen(true);
  };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), duration_minutes: parseInt(form.duration_minutes), images: form.images.filter(Boolean) };
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success(editing ? t("service_updated") : t("service_created"));
      setOpen(false); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || t("err_generic")); }
  };

  const del = async (id) => { if (!window.confirm(t("confirm_delete"))) return; await api.delete(`/services/${id}`); loadAll(); };
  const updateStatus = async (bid, status) => { await api.put(`/bookings/${bid}/status`, { status }); loadAll(); };

  const revenue = bookings.filter((b) => b.payment_status === "paid").reduce((a, b) => a + (b.provider_earnings || 0), 0);

  if (!user?.provider_approved) {
    return <ProviderApplicationStatus user={user} refresh={refresh} />;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-4xl">{t("provider_dash")}</h1>
          <p className="text-stone-600 text-sm">{t("welcome")}, {user?.name}</p>
        </div>
        <Button data-testid="new-service" onClick={openNew} className="rounded-full bg-stone-900 text-stone-50"><Plus size={16} className="mr-2" />{t("new_service")}</Button>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mt-8">
        <StatCard label={t("total_revenue")} value={fmtPrice(revenue)} hint={t("after_commission")} />
        <StatCard label={t("active_services")} value={services.filter((s) => s.status === "published").length} />
        <StatCard label={t("bookings")} value={bookings.length} />
        <StatCard label={t("completed_jobs")} value={bookings.filter((b) => b.status === "completed").length} />
      </div>

      <div className="mt-10 flex gap-4 border-b border-stone-200">
        {[["overview",t("tab_overview")],["services",t("tab_services")],["bookings",t("tab_bookings")]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`} className={`pb-3 text-sm ${tab === k ? "border-b-2 border-stone-900 font-medium" : "text-stone-500"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && <div className="mt-6 text-stone-600 text-sm">{t("overview_hint")}</div>}

      {tab === "services" && (
        <div className="mt-6 bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
          {services.length === 0 && <div className="p-8 text-center text-stone-500 text-sm">{t("no_services_click")}</div>}
          {services.map((s) => (
            <div key={s.id} className="p-5 flex items-center gap-4">
              <img src={s.images?.[0]} className="w-14 h-14 rounded-lg object-cover" alt="" />
              <div className="flex-1">
                <div className="font-medium">{s.title}</div>
                <div className="text-xs text-stone-500">{fmtPrice(s.price)} · {s.duration_minutes} min · <span className="capitalize">{s.status.replace("_"," ")}</span></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => openEdit(s)}>{t("edit")}</Button>
              <Button variant="ghost" size="sm" onClick={() => del(s.id)} className="text-red-600">{t("delete_short")}</Button>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="mt-6 bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
          {bookings.map((b) => (
            <div key={b.id} className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex-1">
                <div className="font-medium">{b.service_title}</div>
                <div className="text-xs text-stone-500">{b.customer?.name} · {new Date(b.scheduled_at).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}</div>
              </div>
              <div className="text-sm font-serif">{fmtPrice(b.provider_earnings)} <span className="text-xs text-stone-500">{t("received")}</span></div>
              <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                <SelectTrigger className="w-40 rounded-full"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["pending","confirmed","in_progress","completed","cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? t("edit_label") : t("new_label")} {t("services").toLowerCase()}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("title_label")}</Label><Input data-testid="svc-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>{t("description")}</Label><Textarea data-testid="svc-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("category")}</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="svc-cat"><SelectValue placeholder={t("category")} /></SelectTrigger>
                  <SelectContent className="bg-white">{cats.map((c) => <SelectItem key={c.id} value={c.id}>{catName(c, lang)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("city")}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label>{t("price_eur")}</Label><Input data-testid="svc-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div><Label>{t("duration_lbl")}</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></div>
            </div>
            <div><Label>{t("image_url")}</Label><Input value={form.images[0] || ""} onChange={(e) => setForm({ ...form, images: [e.target.value] })} /></div>
            <Button data-testid="svc-save" onClick={save} className="w-full bg-stone-900 text-stone-50">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AdminDashboard() {
  useSEO({ title: "Admin | Refine Touch USA", robots: "noindex, nofollow" });
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [commission, setCommission] = useState(0.15);
  const [tab, setTab] = useState("requests");

  const load = async () => {
    const [s, u, sv] = await Promise.all([
      api.get("/admin/stats").then((r) => r.data),
      api.get("/admin/users").then((r) => r.data),
      api.get("/admin/services").then((r) => r.data),
    ]);
    setStats(s); setUsers(u); setServices(sv); setCommission(s.commission_rate);
  };
  useEffect(() => { load(); }, []);

  const saveCommission = async () => {
    await api.put("/admin/settings", { platform_commission: parseFloat(commission) });
    toast.success(t("commission_saved"));
    load();
  };

  if (!stats) return <div className="p-10">{t("loading")}</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-serif text-4xl">{t("admin_title")}</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <StatCard label={t("users")} value={stats.total_users} hint={`${stats.customers} ${t("customers_hint")} · ${stats.providers} ${t("providers_hint")}`} />
        <StatCard label={t("bookings")} value={stats.total_bookings} hint={`${stats.completed_bookings} ${t("completed_hint")}`} />
        <StatCard label={t("revenue")} value={fmtPrice(stats.revenue)} />
        <StatCard label={t("rt_commission")} value={fmtPrice(stats.commission_total)} hint={`${t("rate")}: ${(stats.commission_rate*100).toFixed(0)}%`} />
      </div>

      <div className="mt-10 flex gap-4 border-b border-stone-200 overflow-x-auto">
        {[["requests",t("pr_tab")],["overview",t("tab_overview")],["users",t("users")],["services",t("services")],["waitlist",t("waitlist_tab")],["settings",t("settings")]].map(([k,l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`pb-3 text-sm whitespace-nowrap inline-flex items-center gap-2 ${tab === k ? "border-b-2 border-stone-900 font-medium" : "text-stone-500"}`}>
            {l}
            {k === "requests" && stats.pending_providers > 0 && (
              <span data-testid="pr-badge" className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-medium inline-flex items-center justify-center">{stats.pending_providers}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "requests" && <ProviderRequests onChanged={load} />}
      {tab === "waitlist" && <Waitlist />}

      {tab === "overview" && (
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="bg-white border rounded-2xl p-6">
            <div className="eyebrow text-stone-500 text-xs">{t("pending_approvals")}</div>
            <div className="mt-3 space-y-1 text-sm">
              <div>{stats.pending_providers} {t("pending_providers_x")}</div>
              <div>{stats.pending_services} {t("pending_services_x")}</div>
            </div>
          </div>
          <div className="bg-white border rounded-2xl p-6">
            <div className="eyebrow text-stone-500 text-xs">{t("provider_payouts")}</div>
            <div className="font-serif text-3xl mt-2">{fmtPrice(stats.provider_payouts)}</div>
          </div>
        </div>
      )}

      {tab === "users" && (
        <div className="mt-6 bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
          {users.map((u) => (
            <div key={u.id} className="p-4 flex items-center gap-4">
              <div className="flex-1"><div className="font-medium text-sm">{u.name}</div><div className="text-xs text-stone-500">{u.email} · {u.role}</div></div>
              {u.role === "provider" && !u.provider_approved && (
                <Button data-testid={`approve-${u.id}`} size="sm" onClick={async () => { await api.post(`/admin/users/${u.id}/approve`); load(); }}>{t("approve")}</Button>
              )}
              <span className={`text-xs px-2 py-1 rounded-full ${u.provider_approved ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>{u.provider_approved ? t("active") : t("pending")}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "services" && (
        <div className="mt-6 bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
          {services.map((s) => (
            <div key={s.id} className="p-4 flex items-center gap-4">
              <img src={s.images?.[0]} className="w-12 h-12 rounded-lg object-cover" alt="" />
              <div className="flex-1"><div className="font-medium text-sm">{s.title}</div><div className="text-xs text-stone-500">{s.provider?.name} · {fmtPrice(s.price)} · {s.status}</div></div>
              {s.status === "pending_approval" && (
                <>
                  <Button data-testid={`approve-svc-${s.id}`} size="sm" onClick={async () => { await api.post(`/admin/services/${s.id}/approve`); load(); }}>{t("approve")}</Button>
                  <Button size="sm" variant="outline" onClick={async () => { await api.post(`/admin/services/${s.id}/reject`); load(); }}>{t("reject")}</Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "settings" && (
        <div className="mt-6 bg-white border rounded-2xl p-6 max-w-md">
          <Label>{t("commission_label")}</Label>
          <Input data-testid="commission-input" type="number" step="0.01" min="0" max="1" value={commission} onChange={(e) => setCommission(e.target.value)} className="mt-2" />
          <Button data-testid="save-commission" onClick={saveCommission} className="mt-4 bg-stone-900 text-stone-50">{t("save")}</Button>
        </div>
      )}
    </div>
  );
}
